import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Ford GT40 Mk I (~1966) — the Le Mans mid-engine racer at true scale
// (1 unit = 1 metre): length ~4.20, width 1.78, height a famously LOW 1.02
// ("40 inches tall"), wheelbase 2.41 (front axle +1.15, rear -1.26), ~0.32 m
// wheels. Nose is +Z. Authored for the `cars` showcase batch.
//
// The GT40 is the LOWEST car in the set. It must read as a LOW WIDE LETTERBOX:
// the whole body is barely taller than the wheels, and the little bubble roof
// nestles into the bodywork only a hand's-breadth above the fenders. In profile
// it is ONE continuous low sweep — a very low rounded nose, over a tiny low
// canopy set forward-of-centre, into a long low louvred engine deck falling to a
// blunt Kamm tail — with NO step up over the front wheel. The masses are
// voluptuous and rounded (blobs for fender crowns and rear haunches); the deck
// is a shallow valley between two rounded haunches, never a flat open bed. The
// track is wide with the tyres planted at the outer corners in wide arches, and
// bold dark OVAL intakes sit on the low flanks ahead of the rear wheels. Every
// square view reads dramatically wider than tall (~2.3:1).
//
// Racing livery: Gulf light BLUE on the "paint" slot with the iconic ORANGE
// centre stripe (unslotted) running nose -> bonnet -> roof -> engine deck ->
// tail down the centreline, plus white number roundels on the doors. `drive`
// spins the wheels and `hazards` blinks four indicators.

const PAINT = "#6fb8d6"; // Gulf light blue — the "paint" slot default
const STRIPE = "#e2761f"; // Gulf orange centre stripe (unslotted — always orange)
const ROUNDEL = "#eef2f4"; // white door number roundel
const TRIM = "#141518"; // gloss black (splitter, pillars, panels)
const CLAD = "#202225"; // lower valances, sills, diffuser
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1b2129"; // smoked side / rear glazing
const SCREEN = "#2a3742"; // wraparound windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const MESH = "#0d0e10"; // engine / intake mesh (near-black)
const COVERGLASS = "#3a4a52"; // clear headlamp cover tint
const SPOKE = "#d7dce1"; // bright racing multi-spoke alloys
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#c41726"; // round tail lamps
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). The car is EXTREMELY low and wide: the belt sits
// at ~0.47, the engine deck runs ~0.67-0.74 (barely above the 0.64 wheel tops)
// and the little bubble roof peaks at only ~0.80, while the wide track puts the
// tyres out at ~0.92, so every square view is a low wide letterbox (~2.3:1).
// Authored deliberately low so the perspective preview camera (which exaggerates
// height by showing the long top deck) still reads it as ground-hugging.
const WHEEL_R = 0.32;
const AXLE_F = 1.15;
const AXLE_R = -1.26; // wheelbase 2.41
const WHEEL_X_F = 0.79; // half track, front — tyres out at the corners
const WHEEL_X_R = 0.78; // half track, rear
const TYRE_W_F = 0.26;
const TYRE_W_R = 0.32; // fat rear racing tyres

const SCREEN_RAKE = 0.85; // hard-raked wraparound windscreen (top toward the tail)
const HOOD_SLOPE = 0.05; // bonnet lid diving to the low rounded nose

// The unbroken descending deck: three shallow tilted boxes whose top faces meet
// on shared breakpoints so the low roof falls to the Kamm tail in ONE line.
const SAIL_ANGLE = 0.08; // backlight sail dropping onto the engine deck
const DECK1_ANGLE = 0.045; // louvred engine cover falling toward the tail
const DECK2_ANGLE = 0.05; // tail deck, the last shallow fall

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the low rounded fender crowns and the wide rounded rear haunch shoulders that
 *  make the GT40 read as a voluptuous low racer rather than a slab. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One bright racing multi-spoke alloy over a fat tyre: tyre, brake disc, dark
 *  dish, six bright spokes proud of the tyre face, a chrome knock-off cap with a
 *  little spinner nub. Every part shares `name` so the `drive` track spins them
 *  as one node about the axle. `side` is +1 for the right (+x) wheel, -1 left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 20, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.13, 0.13, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.21, 0.21, 0.06, 20, DISH, [side * (outer - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 6; k++) {
    parts.push(
      box(0.045, 0.38, 0.08, SPOKE, [side * (outer - 0.02), y, z], [(k * Math.PI) / 6, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.07, 0.07, 0.03, 12, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name })); // knock-off cap
  parts.push(cyl(0.018, 0.05, 0.06, 4, CHROME, [side * (outer + 0.03), y, z], [0, 0, Math.PI / 2], { name })); // spinner nub
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over the tyre. The [0, pi/2, 0] yaw puts
 *  the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair flanks the nose intake; rear pair sits beside the tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.62, 0.33, 2.02], size: [0.12, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.62, 0.33, 2.02], size: [0.12, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.74, 0.5, -2.04], size: [0.09, 0.06, 0.03] },
  { name: "indicator-rr", pos: [0.74, 0.5, -2.04], size: [0.09, 0.06, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (WIDE track — fat rear racing tyres planted at the corners) -
  P.push(
    ...wheel(-1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.83, 0.4, 0.38, 0.05)); // wide front arch
    P.push(...arch(s, AXLE_R, 0.85, 0.44, 0.42, 0.055)); // wider fatter rear arch
  }
  P.push(box(1.6, 0.12, 3.4, TRIM, [0, 0.13, -0.05])); // underbody pan

  // -- Body masses (paint) — a very low, wide tub (belt at only ~0.47) ---------
  P.push(paint(1.62, 0.32, 3.3, [0, 0.31, -0.05])); // main tub, sill to belt line

  // Very low rounded nose: a low front prow face, a bonnet lid diving to it, and
  // low rounded fender crowns beside the lid — the whole front is one soft low
  // falling line, no tall hood.
  P.push(paint(1.5, 0.18, 0.16, [0, 0.34, 2.0])); // low front prow face
  P.push(paint(1.38, 0.06, 1.05, [0, 0.43, 1.32], [HOOD_SLOPE, 0, 0])); // bonnet lid diving to the nose
  for (const s of S) P.push(blob([0.22, 0.08, 0.62], [s * 0.66, 0.43, 1.35])); // low rounded fender crowns
  P.push(paint(1.55, 0.22, 0.5, [0, 0.25, 1.8])); // lower nose, bridging tub to prow (no under-gap)
  P.push(box(1.6, 0.06, 0.18, TRIM, [0, 0.16, 2.03])); // low front splitter under the prow
  P.push(box(1.5, 0.14, 0.3, CLAD, [0, 0.19, 1.94])); // front valance filling back to the arch

  // -- Rear haunches (the widest, rounded hips OUTBOARD of the cabin) ----------
  // Quarter-panels over the fat rear wheels with a rounded shoulder blob on top,
  // rising just ABOVE the deck valley so the deck is never a flat open bed.
  for (const s of S) {
    P.push(paint(0.34, 0.4, 1.6, [s * 0.71, 0.42, -0.9])); // haunch body, outer edge ~0.88
    P.push(blob([0.21, 0.11, 0.78], [s * 0.67, 0.58, -0.85])); // rounded haunch shoulder, just above the deck
  }

  // -- Engine bay block under the sloping cover (never a see-through deck) -----
  P.push(box(1.2, 0.38, 1.6, MESH, [0, 0.43, -0.95])); // dark engine bay mass under the cover

  // -- The unbroken low deck behind the cabin: backlight sail -> louvred engine
  //    cover -> tail deck, each shallow tilted box overlapping the next so the
  //    low roof falls to the blunt Kamm tail in ONE line, NO flat patch.
  P.push(paint(1.0, 0.3, 0.6, [0, 0.57, -0.4], [-SAIL_ANGLE, 0, 0])); // backlight sail (top ~0.72)
  P.push(paint(1.5, 0.28, 0.8, [0, 0.545, -1.05], [-DECK1_ANGLE, 0, 0])); // louvred engine cover deck (top ~0.68)
  P.push(paint(1.58, 0.26, 0.7, [0, 0.525, -1.725], [-DECK2_ANGLE, 0, 0])); // tail deck, lands low (top ~0.65)

  // Rear glass lying on the sail, with mesh louvre panels flanking the centre
  // stripe on the engine cover (the mid-engine cue).
  P.push(box(0.86, 0.03, 0.42, GLASS, [0, 0.71, -0.36], [-SAIL_ANGLE, 0, 0])); // rear window
  for (const s of S) {
    P.push(box(0.42, 0.02, 0.5, MESH, [s * 0.44, 0.7, -1.05], [-DECK1_ANGLE, 0, 0])); // louvre panel base
    for (let i = 0; i < 4; i++) {
      const z = -0.85 - 0.13 * i; // louvre slats riding the falling cover slope
      const y = 0.715 + DECK1_ANGLE * (z + 0.85);
      P.push(box(0.44, 0.02, 0.03, "#26282c", [s * 0.44, y, z], [-DECK1_ANGLE, 0, 0]));
    }
  }
  // A small ducktail lip at the very tail — the GT40's subtle rear aero.
  P.push(paint(1.5, 0.05, 0.16, [0, 0.64, -1.96]));

  // -- Greenhouse (a TINY, LOW bubble nestled into the body, set forward) -------
  // The roof peaks at only ~0.80 — a hand's-breadth above the fenders/deck.
  P.push(box(1.02, 0.32, 0.04, SCREEN, [0, 0.56, 0.5], [-SCREEN_RAKE, 0, 0])); // wraparound windscreen
  for (const s of S) P.push(paint(0.09, 0.3, 0.09, [s * 0.47, 0.56, 0.5], [-SCREEN_RAKE, 0, 0])); // A-pillars
  P.push(paint(0.96, 0.05, 0.56, [0, 0.72, 0.08])); // small low roof, edges overlap screen + sail
  P.push(box(0.9, 0.24, 0.66, CABIN, [0, 0.55, 0.07])); // dark interior block sealing the cabin
  for (const s of S) {
    P.push(box(0.03, 0.16, 0.44, GLASS, [s * 0.46, 0.6, 0.06], [0, 0, s * 0.08])); // side glass, small tumblehome
    P.push(box(0.05, 0.16, 0.07, TRIM, [s * 0.46, 0.6, -0.16], [0, 0, s * 0.08])); // C-pillar closing the bubble
    P.push(box(0.006, 0.02, 0.4, TRIM, [s * 0.445, 0.725, 0.08])); // door-cut-into-roof hint
  }

  // -- Flank intakes + NACA ducts feeding the mid engine ----------------------
  // A bold dark OVAL on each low flank ahead of the rear wheels, framed by body.
  for (const s of S) {
    P.push(box(0.05, 0.2, 0.5, MESH, [s * 0.885, 0.4, -0.4])); // dark oval side intake mouth
    P.push(cyl(0.1, 0.1, 0.05, 16, MESH, [s * 0.885, 0.4, -0.15], [0, 0, Math.PI / 2])); // front rounded end
    P.push(cyl(0.1, 0.1, 0.05, 16, MESH, [s * 0.885, 0.4, -0.65], [0, 0, Math.PI / 2])); // rear rounded end
    P.push(box(0.12, 0.015, 0.22, MESH, [s * 0.56, 0.55, -0.62])); // NACA duct on the flank shoulder
    P.push(box(0.03, 0.1, 2.5, CLAD, [s * 0.835, 0.18, -0.1])); // rocker sill (reaches both arches)
  }

  // -- Front fascia: wide oval intake, round covered headlamps, driving lamps --
  P.push(box(0.78, 0.2, 0.06, MESH, [0, 0.33, 2.06])); // wide oval air intake mouth
  for (const s of S) P.push(cyl(0.1, 0.1, 0.06, 16, MESH, [s * 0.39, 0.33, 2.06], [Math.PI / 2, 0, 0])); // rounded mouth ends
  P.push(box(0.9, 0.03, 0.02, TRIM, [0, 0.33, 2.09])); // mouth splitter bar
  for (const s of S) {
    P.push(box(0.34, 0.15, 0.05, TRIM, [s * 0.55, 0.4, 1.99])); // headlamp housing backing
    P.push(cyl(0.075, 0.075, 0.05, 16, LAMP, [s * 0.62, 0.4, 2.01], [Math.PI / 2, 0, 0])); // outer round headlamp
    P.push(cyl(0.055, 0.055, 0.05, 16, LAMP, [s * 0.47, 0.4, 2.01], [Math.PI / 2, 0, 0])); // inner round headlamp
    P.push(box(0.32, 0.13, 0.02, COVERGLASS, [s * 0.55, 0.4, 2.04])); // clear headlamp cover
    P.push(cyl(0.045, 0.045, 0.04, 12, LAMP, [s * 0.22, 0.26, 2.07], [Math.PI / 2, 0, 0])); // small round driving lamp
  }
  P.push(cyl(0.04, 0.04, 0.02, 16, CHROME, [0, 0.43, 2.03], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.34, 0.09, 0.02, PLATE, [0, 0.23, 2.05])); // small low front plate

  // -- Rear fascia: round tail lamps, mesh grille, central exhausts ------------
  P.push(box(1.5, 0.34, 0.08, TRIM, [0, 0.46, -2.0])); // blunt Kamm tail panel
  for (const s of S) {
    P.push(cyl(0.1, 0.1, 0.05, 16, TAIL_RED, [s * 0.4, 0.5, -2.05], [Math.PI / 2, 0, 0])); // inner round tail lamp
    P.push(cyl(0.1, 0.1, 0.05, 16, TAIL_RED, [s * 0.63, 0.5, -2.05], [Math.PI / 2, 0, 0])); // outer round tail lamp
  }
  P.push(box(0.5, 0.18, 0.03, MESH, [0, 0.48, -2.04])); // central engine mesh grille
  for (let i = -2; i <= 2; i++) {
    P.push(box(0.02, 0.14, 0.02, "#26282c", [i * 0.11, 0.48, -2.06])); // grille slats
  }
  for (const dx of [-0.17, -0.057, 0.057, 0.17]) {
    P.push(cyl(0.062, 0.062, 0.14, 12, EXHAUST, [dx, 0.29, -2.1], [Math.PI / 2, 0, 0])); // central exhaust cluster, poking out
  }
  P.push(box(1.4, 0.14, 0.1, CLAD, [0, 0.19, -2.02])); // rear diffuser valance
  P.push(box(0.34, 0.09, 0.02, PLATE, [-0.34, 0.35, -2.06])); // small rear plate, offset to one side

  // -- Racing livery: orange centre stripe over the whole top centreline -------
  P.push(box(0.3, 0.14, 0.03, STRIPE, [0, 0.37, 2.09])); // stripe wrapping the nose tip
  P.push(box(0.34, 0.02, 1.05, STRIPE, [0, 0.463, 1.32], [HOOD_SLOPE, 0, 0])); // bonnet stripe
  P.push(box(0.34, 0.02, 0.56, STRIPE, [0, 0.757, 0.08])); // roof stripe
  P.push(box(0.34, 0.02, 0.6, STRIPE, [0, 0.732, -0.4], [-SAIL_ANGLE, 0, 0])); // sail stripe
  P.push(box(0.34, 0.02, 0.8, STRIPE, [0, 0.707, -1.05], [-DECK1_ANGLE, 0, 0])); // engine-cover stripe
  P.push(box(0.34, 0.02, 0.7, STRIPE, [0, 0.687, -1.725], [-DECK2_ANGLE, 0, 0])); // tail-deck stripe
  P.push(box(0.34, 0.02, 0.16, STRIPE, [0, 0.67, -1.96])); // ducktail stripe

  // -- Furniture: door roundels, wing mirrors, door handles -------------------
  for (const s of S) {
    P.push(cyl(0.15, 0.15, 0.015, 20, ROUNDEL, [s * 0.815, 0.34, 0.28], [0, 0, Math.PI / 2])); // white number roundel
    P.push(box(0.1, 0.03, 0.04, TRIM, [s * 0.62, 0.5, 0.62])); // mirror stalk overlapping the fender
    P.push(paint(0.06, 0.09, 0.09, [s * 0.7, 0.52, 0.62])); // mirror head
    P.push(box(0.012, 0.07, 0.07, GLASS, [s * 0.735, 0.52, 0.63])); // mirror glass, facing back
    P.push(box(0.02, 0.025, 0.11, CHROME, [s * 0.815, 0.38, -0.1])); // door handle
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const fordGt40 = defineModel({
  id: "ford-gt40",
  name: "Ford GT40",
  batchId: "cars",
  tags: ["car", "ford", "gt40", "racing", "lemans", "supercar", "classic"],
  summary: "A 1966 Ford GT40 Mk I Le Mans mid-engine racer at true scale, in Gulf blue with an orange stripe.",
  description:
    "A ~1966 Ford GT40 Mk I at real 1 unit = 1 metre scale (about 4.20 long, " +
    "1.78 wide, a famously low 1.02 tall — the name means '40 inches tall' — " +
    "2.41 wheelbase). It reads as a low wide letterbox: the whole body is barely " +
    "taller than the wheels and the tiny bubble roof nestles into the bodywork. " +
    "In profile it is one continuous low sweep — a very low rounded nose with a " +
    "wide oval air intake, round covered headlamps and driving lamps over a " +
    "front splitter; a tiny low canopy set forward-of-centre with a wraparound " +
    "raked windscreen; a long low louvred engine deck falling in one line to a " +
    "blunt Kamm tail. The masses are voluptuous and rounded — the deck is a " +
    "shallow valley between two rounded rear haunches, never a flat open bed — " +
    "the track is wide with tyres planted at the corners, and bold dark oval " +
    "flank intakes with NACA ducts feed the mid engine. The tail carries round " +
    "tail lamps, a mesh grille, a central exhaust cluster and a small ducktail " +
    "lip, on bright racing multi-spoke alloys with knock-off caps. Racing " +
    "livery: the iconic Gulf light blue on the 'paint' recolour slot with an " +
    "orange centre stripe running nose to tail and white number roundels on the " +
    "doors; `drive` spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.25,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
